import { DiscountUpdateEvent } from '@theme/events';

export const WELCOME_DISCOUNT = Object.freeze({
  code: 'WELCOME15',
  ttlMs: 24 * 60 * 60 * 1000,
  maxAttempts: 2,
  sourceId: 'iwobbl-welcome-discount',
  storagePrefix: 'iwobbl_welcome_discount_v1',
});

const KEYS = Object.freeze({
  pending: `${WELCOME_DISCOUNT.storagePrefix}:pending`,
  applied: `${WELCOME_DISCOUNT.storagePrefix}:applied`,
  suppressed: `${WELCOME_DISCOUNT.storagePrefix}:suppressed`,
  attempts: `${WELCOME_DISCOUNT.storagePrefix}:attempts`,
  ownership: `${WELCOME_DISCOUNT.storagePrefix}:ownership`,
  checkoutEmail: `${WELCOME_DISCOUNT.storagePrefix}:checkout-email`,
});

function normalizeCode(value) {
  return String(value || '').trim().toUpperCase();
}

function discountEntries(cart) {
  return Array.isArray(cart?.discount_codes)
    ? cart.discount_codes
        .map((entry) => ({
          code: normalizeCode(entry?.code),
          applicable: entry?.applicable,
        }))
        .filter((entry) => entry.code)
    : [];
}

function normalizeCheckoutEmail(value) {
  const email = String(value || '').trim().toLowerCase();
  if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return '';
  return email;
}

class IwobblCheckoutContactPrompt {
  constructor({ windowRef, documentRef }) {
    this.window = windowRef;
    this.document = documentRef;
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  request() {
    const stored = normalizeCheckoutEmail(this.readSession(KEYS.checkoutEmail));
    if (stored) return Promise.resolve(stored);
    if (this.pendingPromise) return this.pendingPromise;

    this.ensureUi();
    this.previousActiveElement =
      this.document.activeElement instanceof this.window.HTMLElement
        ? this.document.activeElement
        : null;
    this.previousBodyOverflow = this.document.body.style.overflow;
    this.document.body.style.overflow = 'hidden';
    this.root.hidden = false;
    this.error.textContent = '';
    this.document.addEventListener('keydown', this.handleKeyDown);

    this.pendingPromise = new Promise((resolve) => {
      this.resolve = resolve;
    });

    this.window.requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
    return this.pendingPromise;
  }

  ensureUi() {
    if (this.root) return;

    const root = this.document.createElement('div');
    root.className = 'iwobbl-checkout-contact';
    root.hidden = true;
    root.innerHTML = `
      <div class="iwobbl-checkout-contact__backdrop" data-checkout-contact-cancel></div>
      <div
        class="iwobbl-checkout-contact__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="IwobblCheckoutContactTitle"
        aria-describedby="IwobblCheckoutContactDescription"
      >
        <p class="iwobbl-checkout-contact__eyebrow">FIRST-ORDER DISCOUNT</p>
        <h2 class="iwobbl-checkout-contact__title" id="IwobblCheckoutContactTitle">
          Continue to secure checkout
        </h2>
        <p class="iwobbl-checkout-contact__description" id="IwobblCheckoutContactDescription">
          Enter the email you will use at checkout so Shopify can verify your first-order
          eligibility and prefill your order contact.
        </p>
        <form data-checkout-contact-form novalidate>
          <label class="iwobbl-checkout-contact__label" for="IwobblCheckoutContactEmail">
            Checkout email
          </label>
          <input
            class="iwobbl-checkout-contact__input"
            id="IwobblCheckoutContactEmail"
            type="email"
            autocomplete="email"
            inputmode="email"
            required
          >
          <p class="iwobbl-checkout-contact__error" data-checkout-contact-error role="alert"></p>
          <button class="iwobbl-checkout-contact__continue" type="submit">
            CONTINUE TO CHECKOUT
          </button>
          <button class="iwobbl-checkout-contact__cancel" type="button" data-checkout-contact-cancel>
            CANCEL
          </button>
          <p class="iwobbl-checkout-contact__note">
            This is used for your order and discount check only. It does not subscribe you
            to marketing emails.
          </p>
        </form>
      </div>
    `;
    this.document.body.appendChild(root);

    this.root = root;
    this.input = root.querySelector('#IwobblCheckoutContactEmail');
    this.error = root.querySelector('[data-checkout-contact-error]');
    root.querySelector('[data-checkout-contact-form]').addEventListener('submit', (event) => {
      event.preventDefault();
      const email = normalizeCheckoutEmail(this.input.value);
      if (!email) {
        this.error.textContent = 'Enter a valid checkout email.';
        this.input.setAttribute('aria-invalid', 'true');
        this.input.focus();
        return;
      }

      this.input.removeAttribute('aria-invalid');
      this.writeSession(KEYS.checkoutEmail, email);
      this.finish(email);
    });
    root.querySelectorAll('[data-checkout-contact-cancel]').forEach((button) => {
      button.addEventListener('click', () => this.finish(null));
    });
  }

  handleKeyDown(event) {
    if (event.key !== 'Escape' || this.root?.hidden) return;
    event.preventDefault();
    this.finish(null);
  }

  finish(value) {
    if (!this.pendingPromise) return;

    this.root.hidden = true;
    this.document.body.style.overflow = this.previousBodyOverflow || '';
    this.document.removeEventListener('keydown', this.handleKeyDown);
    this.previousActiveElement?.focus?.({ preventScroll: true });

    const resolve = this.resolve;
    this.resolve = null;
    this.pendingPromise = null;
    resolve(value);
  }

  readSession(key) {
    try {
      return this.window.sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  writeSession(key, value) {
    try {
      this.window.sessionStorage.setItem(key, value);
    } catch (_error) {}
  }
}

export class IwobblWelcomeDiscountController {
  constructor(options = {}) {
    this.window = options.windowRef || window;
    this.document = options.documentRef || document;
    this.fetch = options.fetchFn || this.window.fetch.bind(this.window);
    this.now = options.now || Date.now;
    this.context = options.context || this.window.IWWelcomeDiscountContext || {};
    this.knownIneligible =
      this.context.customerLoggedIn === true &&
      Number(this.context.customerOrderCount || 0) > 0;

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleDiscountUpdate = this.handleDiscountUpdate.bind(this);
    this.handleCheckoutSubmit = this.handleCheckoutSubmit.bind(this);
    this.checkoutBypassForms = new WeakSet();
  }

  start() {
    this.document.addEventListener('cart:update', this.handleCartUpdate);
    this.document.addEventListener('discount:update', this.handleDiscountUpdate);
    this.document.addEventListener('submit', this.handleCheckoutSubmit, true);

    this.armForEligibleSession();
    if (this.hasManagedState()) void this.reconcile();
  }

  armForEligibleSession() {
    if (
      this.knownIneligible ||
      this.readSession(KEYS.suppressed) === '1' ||
      this.hasManagedState()
    ) {
      return;
    }
    this.writeSession(KEYS.pending, '1');
    this.writeSession(KEYS.attempts, '0');
    this.emitStatus('pending');
  }

  handleCartUpdate(event) {
    if (!this.hasManagedState()) return;
    const cart = event?.detail?.resource;
    void this.reconcile(cart?.discount_codes ? cart : null);
  }

  handleDiscountUpdate(event) {
    if (event?.detail?.sourceId === WELCOME_DISCOUNT.sourceId || !this.hasManagedState()) return;

    const cart = event?.detail?.resource;
    if (!cart?.discount_codes) {
      void this.reconcile();
      return;
    }

    const ownership = this.readOwnership();
    const welcomeCode = this.findWelcomeCode(cart);
    if (ownership && ownership.cartToken === String(cart.token || '') && !welcomeCode) {
      this.clearManagedState({ suppress: true });
      this.emitStatus('removed');
      return;
    }

    void this.reconcile(cart);
  }

  handleCheckoutSubmit(event) {
    const form = event?.target;
    const submitter = event?.submitter;
    if (
      !form ||
      this.checkoutBypassForms.has(form) ||
      normalizeCode(submitter?.name).toLowerCase() !== 'checkout' ||
      this.getCodeForCheckout() !== WELCOME_DISCOUNT.code
    ) {
      if (form && this.checkoutBypassForms.has(form)) this.checkoutBypassForms.delete(form);
      return;
    }

    event.preventDefault();
    void this.prepareCartCheckout().finally(() => {
      this.checkoutBypassForms.add(form);
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter);
      } else if (typeof form.submit === 'function') {
        form.submit();
      }
    });
  }

  getCodeForCheckout() {
    if (this.readSession(KEYS.suppressed) === '1') return null;
    return this.readSession(KEYS.pending) === '1' || this.readSession(KEYS.applied) === '1'
      ? WELCOME_DISCOUNT.code
      : null;
  }

  async prepareCartCheckout() {
    if (this.getCodeForCheckout() !== WELCOME_DISCOUNT.code) {
      return { attempted: false, applied: this.readSession(KEYS.applied) === '1' };
    }

    await this.reconcile();
    if (this.readSession(KEYS.pending) === '1' && this.reconcilePromise) {
      await this.reconcilePromise;
    }
    return {
      attempted: true,
      applied: this.readSession(KEYS.applied) === '1',
    };
  }

  markDraftCheckoutPrepared(result = {}) {
    if (normalizeCode(result.code) !== WELCOME_DISCOUNT.code) return;

    this.removeSession(KEYS.pending);
    this.removeSession(KEYS.attempts);
    if (result.applied === true) {
      this.removeSession(KEYS.suppressed);
      this.writeSession(KEYS.applied, '1');
      this.emitStatus('applied');
      return;
    }

    this.removeSession(KEYS.applied);
    this.writeSession(KEYS.suppressed, '1');
    this.emitStatus(
      ['existing_customer', 'shopify_ineligible'].includes(result.eligibility)
        ? 'ineligible'
        : 'failed'
    );
  }

  hasManagedState() {
    return Boolean(
      this.readSession(KEYS.pending) ||
      this.readSession(KEYS.applied) ||
      this.readLocal(KEYS.ownership)
    );
  }

  async reconcile(cartHint = null) {
    if (this.reconciling) {
      this.reconcileQueued = true;
      return this.reconcilePromise;
    }

    this.reconciling = true;
    this.reconcilePromise = (async () => {
      try {
        const cart = cartHint || (await this.fetchCart());
        await this.reconcileCart(cart);
      } catch (_error) {
        this.handleTransientFailure();
      } finally {
        this.reconciling = false;
        this.reconcilePromise = null;
        if (this.reconcileQueued) {
          this.reconcileQueued = false;
          void this.reconcile();
        }
      }
    })();
    return this.reconcilePromise;
  }

  async reconcileCart(cart) {
    const cartToken = String(cart?.token || '');
    const ownership = this.readOwnership();
    const welcomeCode = this.findWelcomeCode(cart);

    if (this.knownIneligible) {
      if (welcomeCode) {
        await this.removeManagedCode(cart, { status: 'ineligible', suppress: true });
      } else {
        this.clearManagedState({ suppress: true });
        this.emitStatus('ineligible');
      }
      return;
    }

    if (ownership) {
      if (!cartToken || ownership.cartToken !== cartToken) {
        this.removeLocal(KEYS.ownership);
        this.removeSession(KEYS.applied);
        this.removeSession(KEYS.attempts);
      } else if (ownership.expiresAt <= this.now()) {
        await this.removeManagedCode(cart, { status: 'expired', suppress: false });
        return;
      } else if (!welcomeCode) {
        this.removeLocal(KEYS.ownership);
        this.removeSession(KEYS.applied);
      } else if (welcomeCode.applicable === false) {
        this.removeSession(KEYS.applied);
        this.writeSession(KEYS.pending, '1');
        this.emitStatus('verification');
        return;
      } else {
        this.removeSession(KEYS.pending);
        this.writeSession(KEYS.applied, '1');
        return;
      }
    }

    if (this.readSession(KEYS.pending) !== '1') return;
    if (this.readSession(KEYS.suppressed) === '1') {
      this.removeSession(KEYS.pending);
      return;
    }

    if (Number(cart?.item_count || 0) <= 0) {
      this.emitStatus('pending');
      return;
    }

    if (welcomeCode) {
      if (welcomeCode.applicable === false) {
        this.removeSession(KEYS.applied);
        this.writeSession(KEYS.pending, '1');
        this.writeOwnership(cart);
        this.emitStatus('verification');
      } else {
        this.removeSession(KEYS.pending);
        this.writeSession(KEYS.applied, '1');
        this.emitStatus('applied');
      }
      return;
    }

    const hasAnotherCode = discountEntries(cart).some(
      (entry) => entry.code !== WELCOME_DISCOUNT.code
    );
    if (hasAnotherCode) {
      this.clearPending({ suppress: true });
      this.emitStatus('conflict');
      return;
    }

    await this.applyToCart(cart);
  }

  async applyToCart(cart) {
    const attempts = Number(this.readSession(KEYS.attempts) || 0) + 1;
    this.writeSession(KEYS.attempts, String(attempts));

    try {
      const updatedCart = await this.updateDiscounts([WELCOME_DISCOUNT.code]);
      const appliedCode = this.findWelcomeCode(updatedCart);
      if (!appliedCode) {
        this.clearPending({ suppress: true });
        try {
          const cleanedCart = await this.removeCodeFromCart(updatedCart);
          this.dispatchCartDiscountUpdate(cleanedCart);
        } catch (_error) {
          // The terminal state prevents re-application; an owned cart record was never created.
        }
        this.emitStatus('failed');
        return;
      }

      this.writeOwnership(updatedCart, cart);
      this.removeSession(KEYS.suppressed);
      this.dispatchCartDiscountUpdate(updatedCart);
      if (appliedCode.applicable === false) {
        this.removeSession(KEYS.applied);
        this.writeSession(KEYS.pending, '1');
        this.emitStatus('verification');
      } else {
        this.removeSession(KEYS.pending);
        this.writeSession(KEYS.applied, '1');
        this.emitStatus('applied');
      }
    } catch (_error) {
      if (attempts >= WELCOME_DISCOUNT.maxAttempts) {
        this.clearPending({ suppress: true });
        this.emitStatus('failed');
      } else {
        this.emitStatus('pending');
      }
    }
  }

  async removeManagedCode(cart, { status, suppress }) {
    try {
      const updatedCart = await this.removeCodeFromCart(cart);
      this.clearManagedState({ suppress });
      this.dispatchCartDiscountUpdate(updatedCart);
      this.emitStatus(status);
    } catch (_error) {
      this.emitStatus('failed');
    }
  }

  async removeCodeFromCart(cart) {
    const remainingCodes = discountEntries(cart)
      .map((entry) => entry.code)
      .filter((code) => code !== WELCOME_DISCOUNT.code);
    return this.updateDiscounts(remainingCodes);
  }

  async fetchCart() {
    const response = await this.fetch(this.cartUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart_read_${response.status}`);
    return response.json();
  }

  async updateDiscounts(codes) {
    const response = await this.fetch(this.cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    if (!response.ok) throw new Error(`discount_update_${response.status}`);
    return response.json();
  }

  cartUrl() {
    return `${this.routesRoot()}cart.js`;
  }

  cartUpdateUrl() {
    return this.window.Theme?.routes?.cart_update_url || `${this.routesRoot()}cart/update.js`;
  }

  routesRoot() {
    const root = String(this.window.Shopify?.routes?.root || '/');
    return root.endsWith('/') ? root : `${root}/`;
  }

  findWelcomeCode(cart) {
    return discountEntries(cart).find((entry) => entry.code === WELCOME_DISCOUNT.code) || null;
  }

  writeOwnership(primaryCart, fallbackCart = null) {
    const cartToken = String(primaryCart?.token || fallbackCart?.token || '');
    if (!cartToken) return;
    this.writeLocal(
      KEYS.ownership,
      JSON.stringify({
        code: WELCOME_DISCOUNT.code,
        cartToken,
        appliedAt: this.now(),
        expiresAt: this.now() + WELCOME_DISCOUNT.ttlMs,
      })
    );
  }

  readOwnership() {
    try {
      const parsed = JSON.parse(this.readLocal(KEYS.ownership) || 'null');
      if (
        normalizeCode(parsed?.code) !== WELCOME_DISCOUNT.code ||
        !parsed?.cartToken ||
        !Number.isFinite(Number(parsed?.expiresAt))
      ) {
        this.removeLocal(KEYS.ownership);
        return null;
      }
      return {
        code: WELCOME_DISCOUNT.code,
        cartToken: String(parsed.cartToken),
        appliedAt: Number(parsed.appliedAt || 0),
        expiresAt: Number(parsed.expiresAt),
      };
    } catch (_error) {
      this.removeLocal(KEYS.ownership);
      return null;
    }
  }

  clearPending({ suppress }) {
    this.removeSession(KEYS.pending);
    this.removeSession(KEYS.applied);
    this.removeSession(KEYS.attempts);
    if (suppress) this.writeSession(KEYS.suppressed, '1');
  }

  clearManagedState({ suppress }) {
    this.removeLocal(KEYS.ownership);
    this.clearPending({ suppress });
  }

  handleTransientFailure() {
    if (this.readSession(KEYS.pending) !== '1') return;
    const attempts = Number(this.readSession(KEYS.attempts) || 0) + 1;
    this.writeSession(KEYS.attempts, String(attempts));
    if (attempts >= WELCOME_DISCOUNT.maxAttempts) {
      this.clearPending({ suppress: true });
      this.emitStatus('failed');
    } else {
      this.emitStatus('pending');
    }
  }

  dispatchCartDiscountUpdate(cart) {
    this.document.dispatchEvent(new DiscountUpdateEvent(cart, WELCOME_DISCOUNT.sourceId));
  }

  emitStatus(status) {
    const EventConstructor = this.window.CustomEvent || CustomEvent;
    this.window.dispatchEvent(
      new EventConstructor('iwobbl:welcome-discount', {
        detail: {
          status,
          code: WELCOME_DISCOUNT.code,
        },
      })
    );
  }

  readLocal(key) {
    try {
      return this.window.localStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  writeLocal(key, value) {
    try {
      this.window.localStorage.setItem(key, value);
    } catch (_error) {}
  }

  removeLocal(key) {
    try {
      this.window.localStorage.removeItem(key);
    } catch (_error) {}
  }

  readSession(key) {
    try {
      return this.window.sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  writeSession(key, value) {
    try {
      this.window.sessionStorage.setItem(key, value);
    } catch (_error) {}
  }

  removeSession(key) {
    try {
      this.window.sessionStorage.removeItem(key);
    } catch (_error) {}
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const controller = new IwobblWelcomeDiscountController();
  const checkoutContact = new IwobblCheckoutContactPrompt({
    windowRef: window,
    documentRef: document,
  });
  window.IWWelcomeDiscount = Object.freeze({
    getCodeForCheckout: () => controller.getCodeForCheckout(),
    prepareCartCheckout: () => controller.prepareCartCheckout(),
    requestCheckoutEmail: () => checkoutContact.request(),
    markDraftCheckoutPrepared: (result) => controller.markDraftCheckoutPrepared(result),
  });
  controller.start();
}
